
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, AlertTriangle, Info, X, Loader2, Zap } from 'lucide-react';
import { cn } from '../lib/utils';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface FeedbackContextType {
  showToast: (message: string, type?: ToastType) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <FeedbackContext.Provider value={{ showToast, isLoading, setIsLoading }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-20 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.95 }}
              className={cn(
                "pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-2xl min-w-[300px] max-w-md backdrop-blur-md",
                toast.type === 'success' && "bg-emerald-500/10 border-emerald-500/20 text-emerald-500",
                toast.type === 'error' && "bg-rose-500/10 border-rose-500/20 text-rose-500",
                toast.type === 'info' && "bg-blue-500/10 border-blue-500/20 text-blue-500"
              )}
            >
              <div className="shrink-0">
                {toast.type === 'success' && <CheckCircle2 size={18} />}
                {toast.type === 'error' && <AlertTriangle size={18} />}
                {toast.type === 'info' && <Info size={18} />}
              </div>
              <p className="flex-1 text-xs font-black uppercase italic tracking-tight leading-tight">
                {toast.message}
              </p>
              <button 
                onClick={() => removeToast(toast.id)}
                className="shrink-0 p-1 hover:bg-black/5 rounded-lg transition-colors"
              >
                <X size={14} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Global Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[110] flex items-center justify-center pointer-events-auto"
          >
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full border-2 border-emerald-500/20 border-t-emerald-500 animate-spin" />
                <Zap size={20} className="absolute inset-0 m-auto text-emerald-500 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="text-sm font-black text-[var(--app-text)] uppercase italic tracking-widest">Processing</p>
                <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Executing Neural Strategy</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}
